import { useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'

interface TweetNode {
  id: string
  label: string
  type: 'tweet' | 'user' | 'mention' | 'hashtag'
  tweetId?: string
  username?: string
  engagement?: number
}

interface TweetEdge {
  source: string
  target: string
  type: 'mentions' | 'retweets' | 'replies' | 'hashtag'
  weight?: number
}

interface XTweetLinkAnalysisGraphProps {
  tweets: any[]
  centralUsername?: string
  onNodeClick?: (node: TweetNode) => void
  height?: number
}

const extractTweetId = (tweet: any, fallbackIndex: number) => {
  const rawId = tweet?.id_str ?? tweet?.id ?? tweet?.tweetId ?? tweet?.tweet_id ?? tweet?.clientTweetId
  return rawId ? String(rawId) : `tweet-${fallbackIndex}`
}

const extractTweetText = (tweet: any) =>
  tweet?.text || tweet?.fullText || tweet?.content || tweet?.description || tweet?.message || ''

const getAuthorUsername = (tweet: any) =>
  tweet?.user?.userName || tweet?.user?.username || tweet?.user?.screenName ||
  tweet?.username || tweet?.handle || tweet?.author?.username || tweet?.author?.userName || 'unknown'

const normalizeMentions = (tweet: any): string[] => {
  const raw: unknown = tweet?.mentions || tweet?.entities?.user_mentions || []
  if (Array.isArray(raw)) {
    const result = (raw as any[]).map((m: any) => String(typeof m === 'string' ? m.replace(/^@/, '') : m?.username || m?.screen_name || ''))
    return result.filter((x) => x.length > 0) as string[]
  }
  const text = extractTweetText(tweet)
  const matches: string[] = (text.match(/@(\w+)/g) || []) as string[]
  return Array.from(new Set(matches.map((m) => m.replace('@', ''))))
}

const normalizeHashtags = (tweet: any): string[] => {
  const raw: unknown = tweet?.hashtags || tweet?.entities?.hashtags || []
  if (Array.isArray(raw)) {
    const result = (raw as any[]).map((h: any) => String(typeof h === 'string' ? h.replace(/^#/, '') : h?.tag || h?.text || h?.hashtag || ''))
    return result.filter((x) => x.length > 0) as string[]
  }
  const text = extractTweetText(tweet)
  const matches: string[] = (text.match(/#(\w+)/g) || []) as string[]
  return Array.from(new Set(matches.map((h) => h.replace('#', ''))))
}

const NODE_COLORS: Record<string, string> = {
  tweet: '#10b981',
  user: '#3b82f6',
  mention: '#8b5cf6',
  hashtag: '#f59e0b'
}

export function XTweetLinkAnalysisGraph({
  tweets = [],
  centralUsername,
  onNodeClick,
  height = 500
}: XTweetLinkAnalysisGraphProps) {
  const svgRef = useRef<SVGSVGElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const simulationRef = useRef<d3.Simulation<d3.SimulationNodeDatum, undefined> | null>(null)
  const [selectedNode, setSelectedNode] = useState<TweetNode | null>(null)

  useEffect(() => {
    if (!svgRef.current || !tweets.length) return

    const nodes: TweetNode[] = []
    const edges: TweetEdge[] = []
    const nodeIds = new Set<string>()

    // Central user node
    if (centralUsername) {
      const centralId = `user_${centralUsername}`
      if (!nodeIds.has(centralId)) {
        nodes.push({
          id: centralId,
          label: `@${centralUsername}`,
          type: 'user',
          username: centralUsername
        })
        nodeIds.add(centralId)
      }
    }

    const mentionCounts = new Map<string, number>()
    const hashtagCounts = new Map<string, number>()

    tweets.forEach((tweet, index) => {
      const tweetId = extractTweetId(tweet, index)
      const authorUsername = getAuthorUsername(tweet)
      const text = extractTweetText(tweet)
      const likes = tweet.likeCount ?? tweet.likes ?? 0
      const retweets = tweet.retweetCount ?? tweet.retweets ?? 0
      const replies = tweet.replyCount ?? tweet.replies ?? 0
      const engagement = likes + retweets * 2 + replies

      const tweetNodeId = `tweet_${tweetId}`
      if (!nodeIds.has(tweetNodeId)) {
        nodes.push({
          id: tweetNodeId,
          label: text.length > 30 ? text.substring(0, 27) + '...' : text,
          type: 'tweet',
          tweetId,
          username: authorUsername,
          engagement
        })
        nodeIds.add(tweetNodeId)
      }

      if (centralUsername && authorUsername !== centralUsername) {
        edges.push({ source: `user_${authorUsername}`, target: tweetNodeId, type: 'mentions', weight: 1 })
      }

      const mentions = normalizeMentions(tweet)
      mentions.forEach((m) => {
        mentionCounts.set(m, (mentionCounts.get(m) || 0) + 1)
        const mentionId = `mention_${m}`
        if (!nodeIds.has(mentionId)) {
          nodes.push({ id: mentionId, label: `@${m}`, type: 'mention', username: m })
          nodeIds.add(mentionId)
        }
        edges.push({ source: tweetNodeId, target: mentionId, type: 'mentions', weight: 1 })
      })

      const hashtags = normalizeHashtags(tweet)
      hashtags.forEach((h) => {
        hashtagCounts.set(h, (hashtagCounts.get(h) || 0) + 1)
        const tagId = `hashtag_${h}`
        if (!nodeIds.has(tagId)) {
          nodes.push({ id: tagId, label: `#${h}`, type: 'hashtag' })
          nodeIds.add(tagId)
        }
        edges.push({ source: tweetNodeId, target: tagId, type: 'hashtag', weight: 1 })
      })
    })

    const validEdges = edges.filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target))
    if (nodes.length === 0) return

    const width = containerRef.current?.clientWidth || 700

    d3.select(svgRef.current).selectAll('*').remove()

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .style('background', '#0a0a0a')

    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.2, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform.toString())
      })
    svg.call(zoom as any)

    const g = svg.append('g')

    const simulation = d3.forceSimulation(nodes as d3.SimulationNodeDatum[])
      .force('link', d3.forceLink(validEdges).id((d: any) => d.id).distance(100))
      .force('charge', d3.forceManyBody().strength(-250))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius((d: any) => (d.type === 'tweet' ? 18 : 12)))

    const link = g.append('g')
      .selectAll('line')
      .data(validEdges)
      .enter()
      .append('line')
      .attr('stroke', (d: any) => {
        if (d.type === 'mentions') return '#8b5cf6'
        if (d.type === 'hashtag') return '#f59e0b'
        return '#6b7280'
      })
      .attr('stroke-opacity', 0.5)
      .attr('stroke-width', 1.5)

    const node = g.append('g')
      .selectAll('g')
      .data(nodes)
      .enter()
      .append('g')
      .attr('class', 'node')
      .call(d3.drag<SVGGElement, any>()
        .on('start', (event, d: any) => {
          if (!event.active) simulation.alphaTarget(0.3).restart()
          d.fx = d.x
          d.fy = d.y
        })
        .on('drag', (event, d: any) => {
          d.fx = event.x
          d.fy = event.y
        })
        .on('end', (event, d: any) => {
          if (!event.active) simulation.alphaTarget(0)
          d.fx = null
          d.fy = null
        }) as any
      )
      .on('click', (_, d: any) => {
        setSelectedNode(d)
        if (onNodeClick) onNodeClick(d)
      })

    node.append('circle')
      .attr('r', (d: any) => (d.type === 'tweet' ? 18 : 12))
      .attr('fill', (d: any) => NODE_COLORS[d.type] || '#6b7280')
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')

    node.append('text')
      .text((d: any) => (d.label?.length > 15 ? d.label.substring(0, 12) + '...' : d.label) || d.id)
      .attr('dy', (d: any) => (d.type === 'tweet' ? 28 : 20))
      .attr('text-anchor', 'middle')
      .attr('fill', '#e5e7eb')
      .attr('font-size', '10px')
      .style('pointer-events', 'none')

    simulation.on('tick', () => {
      link
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y)
      node.attr('transform', (d: any) => `translate(${d.x},${d.y})`)
    })

    simulationRef.current = simulation

    return () => {
      simulation.stop()
    }
  }, [tweets, centralUsername, height, onNodeClick])

  return (
    <div ref={containerRef} className="w-full space-y-3">
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
          <span>Tweet</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
          <span>User</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-violet-500" />
          <span>Mention</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
          <span>Hashtag</span>
        </div>
      </div>
      {selectedNode && (
        <div className="rounded-lg border border-emerald-500/50 bg-black/50 p-3 text-sm">
          <p className="font-semibold text-white">{selectedNode.label}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Type: {selectedNode.type} {selectedNode.engagement != null && `· Engagement: ${selectedNode.engagement}`}
          </p>
          {selectedNode.username && (
            <a
              href={`https://x.com/${selectedNode.username}`}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-emerald-400 hover:underline mt-1 inline-block"
            >
              View @{selectedNode.username} on X
            </a>
          )}
        </div>
      )}
      <svg ref={svgRef} className="w-full rounded-lg border border-emerald-500/40" />
    </div>
  )
}
